import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { SortIcon } from '@/UI/Icons';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { I18N_KEYS } from '@/language/keys';
import { FileSortOrder } from '@/types/node';
import type { MenuAction } from '@/components/Menu/Actions';

interface SortOrderMenuProps {
  sortOrder: FileSortOrder;
  onSortOrderChange: (sortOrder: FileSortOrder) => void;
}

export const SortOrderMenu = ({ sortOrder, onSortOrderChange }: SortOrderMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  }, []);

  const items = useMemo<MenuAction[]>(
    () => [
      { type: 'section_label', text: t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.SORT_ORDER_BY) },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.SORT_DATE_ADDED),
        action: () => onSortOrderChange(FileSortOrder.DateAdded),
        selected: sortOrder === FileSortOrder.DateAdded,
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.SORT_NAME),
        action: () => onSortOrderChange(FileSortOrder.Name),
        selected: sortOrder === FileSortOrder.Name,
      },
    ],
    [sortOrder, onSortOrderChange, t],
  );

  return (
    <>
      <Tooltip
        title={t(I18N_KEYS.RECIPE_MAIN.NODES.IMPORT_NODE.SORT_ORDER_BY)}
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
          },
        }}
        disableInteractive
      >
        <AppIconButton onClick={handleOpen} width={32} height={32} mode="on-light">
          <SortIcon />
        </AppIconButton>
      </Tooltip>
      <AppContextMenu
        width="160px"
        open={anchorEl !== null}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        items={items}
      />
    </>
  );
};
